import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { AddVehicleModal } from './AddVehicleModal';
import EditVehicleModal from './EditVehicleModal';
import VehicleCard from './VehicleCard';
import PageLoader from './ui/PageLoader';
import EmptyState, { CarIcon } from './ui/EmptyState';
import Button from './ui/Button';
import { PageHeader } from './ui/PageHeader';
import PageShell from './ui/PageShell';
import { canEditVehicle } from '../utils/permissions';
import { getVehicleCompleteness } from '../utils/vehicleCompleteness';
import { useOnboarding } from '../hooks/useOnboarding';
import {
  OnboardingResumeCard,
  OnboardingWelcomeCard,
  SetupChecklistCard,
} from './onboarding/OnboardingCards';
import {
  buildBuilderChecklist,
  checklistProgress,
  shouldShowBuilderChecklist,
  shouldShowOnboardingResume,
  shouldShowWelcome,
  type ChecklistStep,
} from '../utils/onboarding';

import { vehicleProfilePath, resolveVehicleNavSlug } from '../utils/vehicleUrls';

/**
 * Bilt garage dashboard (`/garage`).
 *
 * Lists vehicles with first-use welcome, setup checklist, and resume card
 * derived from real vehicle/profile state.
 */
const GarageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const onboarding = useOnboarding();

  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const state = location.state as { openAddVehicle?: boolean } | null;
    const params = new URLSearchParams(location.search);
    const openFromQuery = params.get('add') === '1';
    if (state?.openAddVehicle || openFromQuery) {
      setIsAddVehicleModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const userVehicles = await apiService.getUserVehicles();

      const withImages = await Promise.all(
        userVehicles.map(async (vehicle: any) => {
          const displayName =
            vehicle.nickname ||
            `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`.trim();
          try {
            const images = await apiService.getVehicleImages(vehicle.id);
            const hero = images
              .filter((img) => img.image_type === 'hero')
              .sort((a, b) => a.sort_order - b.sort_order);
            return {
              ...vehicle,
              display_name: displayName,
              image_url: hero.length > 0 ? hero[0].image_url : undefined,
              image_count: images.length,
            };
          } catch (imageError) {
            console.error(`Failed to load images for vehicle ${vehicle.id}:`, imageError);
            return { ...vehicle, display_name: displayName, image_count: 0 };
          }
        })
      );

      setVehicles(withImages);
    } catch (err: any) {
      console.error('Failed to load garage:', err);
      setError(err.message || 'Failed to load your garage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadVehicles();
    }
  }, [isAuthenticated, loadVehicles]);

  const {
    loading: onboardingLoading,
    state: onboardingState,
    markStarted,
    markCompleted,
  } = onboarding;

  const checklistSteps = useMemo(() => {
    if (!user) return [];
    const snapshots = vehicles.map((vehicle) => {
      const completeness = getVehicleCompleteness({
        engine: vehicle.engine,
        transmission: vehicle.transmission,
        drivetrain: vehicle.drivetrain,
        chassis: vehicle.chassis,
        description: vehicle.description,
        imageCount: vehicle.image_count ?? 0,
        modificationCount: vehicle.modification_count ?? 0,
      });
      return {
        id: vehicle.id,
        slugPath: (() => {
          const slug = resolveVehicleNavSlug(vehicle);
          return slug && user ? vehicleProfilePath(user.username, slug) : '/garage';
        })(),
        engine: vehicle.engine,
        transmission: vehicle.transmission,
        drivetrain: vehicle.drivetrain,
        chassis: vehicle.chassis,
        description: vehicle.description,
        imageCount: vehicle.image_count ?? 0,
        modificationCount: vehicle.modification_count ?? 0,
        completenessPercent: completeness.percent,
      };
    });
    return buildBuilderChecklist(snapshots, onboardingState, user.username);
  }, [vehicles, onboardingState, user]);

  const showWelcome = shouldShowWelcome(onboardingState, vehicles.length);
  const showChecklist = shouldShowBuilderChecklist(onboardingState, checklistSteps);
  const showResume = shouldShowOnboardingResume(onboardingState, checklistSteps);

  useEffect(() => {
    if (!onboardingLoading && (showWelcome || showChecklist) && !onboardingState.started_at) {
      void markStarted();
    }
  }, [onboardingLoading, onboardingState.started_at, markStarted, showWelcome, showChecklist]);

  useEffect(() => {
    const { done, total } = checklistProgress(checklistSteps);
    if (total > 0 && done === total && !onboardingState.completed_at) {
      void markCompleted();
    }
  }, [checklistSteps, onboardingState.completed_at, markCompleted]);

  const handleVehicleClick = (vehicle: any) => {
    if (!user) return;
    const slug = resolveVehicleNavSlug(vehicle);
    if (!slug) return;
    navigate(vehicleProfilePath(user.username, slug));
  };

  const handleVehicleAdded = (newVehicle: any) => {
    const wasEmpty = vehicles.length === 0;
    setVehicles((prev) => [
      ...prev,
      {
        ...newVehicle,
        display_name:
          newVehicle.nickname ||
          `${newVehicle.year} ${newVehicle.make} ${newVehicle.model} ${newVehicle.trim}`.trim(),
        modification_count: 0,
        image_count: 0,
      },
    ]);
    setIsAddVehicleModalOpen(false);
    void onboarding.dismissWelcome();
    if (wasEmpty) {
      void onboarding.track('onboarding_first_vehicle_created', {
        vehicle_id: newVehicle?.id,
      });
    }
  };

  const handleEditVehicle = (vehicle: any) => {
    setSelectedVehicle({
      ...vehicle,
      owner_username: vehicle.owner_username || user?.username,
    });
    setIsEditVehicleModalOpen(true);
  };

  const handleVehicleUpdated = (updatedVehicle: any) => {
    setVehicles((prev) =>
      prev.map((v) =>
        v.id === updatedVehicle.id
          ? {
              ...v,
              ...updatedVehicle,
              display_name:
                updatedVehicle.nickname ||
                `${updatedVehicle.year} ${updatedVehicle.make} ${updatedVehicle.model} ${updatedVehicle.trim}`.trim(),
            }
          : v
      )
    );
    setIsEditVehicleModalOpen(false);
    setSelectedVehicle(null);
  };

  const handleChecklistAction = (step: ChecklistStep) => {
    if (step.actionId === 'add_vehicle') {
      setIsAddVehicleModalOpen(true);
      return;
    }
    if (step.href) {
      if (step.id === 'profile') {
        void onboarding.markStep('profile_previewed');
      }
      navigate(step.href);
    }
  };

  if (authLoading || (isAuthenticated && loading)) {
    return <PageLoader message="Loading your garage…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <PageShell contentClassName="!py-8 sm:!py-10">
        <PageHeader
          eyebrow="Builds"
          title="Your Garage"
          description={
            vehicles.length === 0
              ? 'One build profile for sharing, events, and history.'
              : vehicles.length === 1
                ? '1 build in your garage'
                : `${vehicles.length} builds in your garage`
          }
          actions={
            vehicles.length > 0 ? (
              <Button
                variant="primary"
                onClick={() => setIsAddVehicleModalOpen(true)}
                className="min-h-[44px] shrink-0"
              >
                Add a Build
              </Button>
            ) : null
          }
        />

        {showWelcome && (
          <OnboardingWelcomeCard
            onAddVehicle={() => setIsAddVehicleModalOpen(true)}
            onExplore={() => {
              void onboarding.dismissWelcome();
              navigate('/discover');
            }}
            onSkip={() => void onboarding.dismissWelcome()}
          />
        )}

        {showChecklist && !showWelcome && (
          <SetupChecklistCard
            title="Your build is taking shape"
            steps={checklistSteps}
            onDismiss={() => void onboarding.dismissChecklist()}
            onStepAction={handleChecklistAction}
          />
        )}

        {showResume && !showWelcome && !showChecklist && (
          <OnboardingResumeCard onContinue={() => void onboarding.showSetupGuide()} />
        )}

        {error ? (
          <EmptyState
            icon={<CarIcon />}
            title="Couldn’t load your garage"
            description={error}
            action={
              <Button variant="primary" onClick={loadVehicles}>
                Try again
              </Button>
            }
          />
        ) : vehicles.length === 0 ? (
          !showWelcome ? (
            <EmptyState
              imageSrc="/images/brand/empty-garage-bay.jpg"
              imageAlt=""
              title="Your garage is empty"
              description="Add a build. Document the mods and media. Share one link or a QR wherever the car shows up."
              action={
                <Button
                  variant="primary"
                  onClick={() => setIsAddVehicleModalOpen(true)}
                  className="min-h-[44px]"
                >
                  Add a Build
                </Button>
              }
            />
          ) : null
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {vehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                onVehicleClick={handleVehicleClick}
                showOwner={false}
                isOwner={user ? canEditVehicle(user, vehicle) : false}
                onEditClick={handleEditVehicle}
                collectionMode
                showFavorite={false}
                discoveryBadges={vehicle.badges}
                discoveryBadgeOverflow={vehicle.badge_overflow || 0}
                completionPercent={
                  getVehicleCompleteness({
                    engine: vehicle.engine,
                    transmission: vehicle.transmission,
                    drivetrain: vehicle.drivetrain,
                    chassis: vehicle.chassis,
                    description: vehicle.description,
                    imageCount: vehicle.image_count ?? 0,
                    modificationCount: vehicle.modification_count ?? 0,
                  }).percent
                }
              />
            ))}
          </div>
        )}

      {isAddVehicleModalOpen && (
        <AddVehicleModal
          isOpen={isAddVehicleModalOpen}
          onClose={() => setIsAddVehicleModalOpen(false)}
          onVehicleAdded={handleVehicleAdded}
        />
      )}

      {isEditVehicleModalOpen && selectedVehicle && (
        <EditVehicleModal
          isOpen={isEditVehicleModalOpen}
          onClose={() => {
            setIsEditVehicleModalOpen(false);
            setSelectedVehicle(null);
          }}
          vehicle={selectedVehicle}
          onVehicleUpdated={handleVehicleUpdated}
        />
      )}
    </PageShell>
  );
};

export default GarageDashboard;
